<template>
  <div class="step-regras">
    <!-- Add Faixa Form -->
    <div class="faixa-form">
      <div class="faixa-form__title">Adicionar faixa de lucratividade</div>
      <div class="faixa-form__row">
        <div class="faixa-form__field faixa-form__field--char">
          <label>Indicador</label>
          <input
            type="text"
            class="form-control faixa-form__char-input"
            v-model="newFaixa.indicador"
            placeholder="A ou 🚀"
            :maxlength="32"
            @input="normalizarIndicador('newFaixa')"
            @keyup.enter="addFaixa"
          />
        </div>

        <div class="faixa-form__field faixa-form__field--label">
          <label>Nome da faixa</label>
          <input
            type="text"
            class="form-control"
            v-model="newFaixa.label"
            placeholder="Ex: Excelente, Margem Mínima..."
            maxlength="100"
            @keyup.enter="addFaixa"
          />
        </div>

        <div class="faixa-form__field faixa-form__field--cor">
          <label>Cor</label>
          <ColorInput v-model="newFaixa.cor" />
        </div>

        <div class="faixa-form__field faixa-form__field--margem">
          <label>Margem mínima (%)</label>
          <input
            type="number"
            class="form-control"
            step="0.01"
            v-model.number="newFaixa.margemMinima"
            placeholder="0.00"
          />
        </div>
      </div>

      <div class="faixa-form__row faixa-form__row--orientacao">
        <div class="faixa-form__field faixa-form__field--orientacao">
          <label>Orientação <span class="faixa-form__optional">(opcional)</span></label>
          <textarea
            ref="newOrientacaoTextarea"
            class="form-control faixa-form__orientacao"
            v-model="newFaixa.orientacao"
            placeholder="Ex: Margem abaixo do ideal. Antes de enviar para análise, tente renegociar com o cliente: ajustar quantidades, oferecer alternativas mais rentáveis ou rever descontos aplicados."
            rows="2"
            maxlength="500"
            @input="autoResize($event.target)"
          ></textarea>
          <span class="faixa-form__counter" :class="{ 'faixa-form__counter--limit': (newFaixa.orientacao || '').length >= 500 }">
            {{ (newFaixa.orientacao || '').length }}/500
          </span>
        </div>
      </div>

      <div class="faixa-form__footer">
        <button
          class="faixa-form__add-btn"
          :disabled="!canAddFaixa"
          @click="addFaixa"
          title="Adicionar faixa"
        >
          <CIcon name="cil-plus" size="sm" />
          Adicionar
        </button>
      </div>
    </div>

    <!-- Faixas List -->
    <div class="faixa-selected">
      <div class="faixa-selected__label">
        Faixas cadastradas ({{ form.regras.length }})
      </div>

      <div v-if="form.regras.length > 0" class="faixa-list">
        <div class="faixa-list__header">
          <span class="faixa-list__col faixa-list__col--cor"></span>
          <span class="faixa-list__col faixa-list__col--char">Ind.</span>
          <span class="faixa-list__col faixa-list__col--label">Nome</span>
          <span class="faixa-list__col faixa-list__col--margem">Margem Mín.</span>
          <span class="faixa-list__col faixa-list__col--status">Status</span>
          <span class="faixa-list__col faixa-list__col--orientacao">Orientação</span>
          <span class="faixa-list__col faixa-list__col--acoes"></span>
        </div>

        <div
          v-for="regra in sortedRegras"
          :key="regra.originalIndex"
          class="faixa-row-wrapper"
          :class="{ 'faixa-row-wrapper--editing': editingIndex === regra.originalIndex }"
        >
          <!-- View Mode -->
          <template v-if="editingIndex !== regra.originalIndex">
            <div class="faixa-row">
              <span class="faixa-row__swatch" :style="{ background: regra.cor }"></span>
              <span class="faixa-row__char">
                <span class="faixa-row__char-badge">{{ regra.indicador || "—" }}</span>
              </span>
              <span class="faixa-row__label" :title="regra.label">{{ regra.label }}</span>
              <span class="faixa-row__margem">
                {{ regra.margemMinima }}%
              </span>
              <span class="faixa-row__status">
                <span
                  v-if="getStatusFaixa(regra.margemMinima)"
                  class="faixa-status-badge"
                  :style="{
                    backgroundColor: getStatusFaixa(regra.margemMinima).bg,
                    color: getStatusFaixa(regra.margemMinima).color,
                    borderColor: getStatusFaixa(regra.margemMinima).color + '30',
                  }"
                >
                  {{ getStatusFaixa(regra.margemMinima).label }}
                </span>
                <span v-else class="faixa-status-badge faixa-status-badge--empty">—</span>
              </span>
              <span class="faixa-row__orientacao-col">
                <button
                  v-if="regra.orientacao"
                  class="faixa-row__view-btn"
                  :class="{ 'faixa-row__view-btn--active': expandedOrientacaoIndex === regra.originalIndex }"
                  @click="toggleOrientacao(regra.originalIndex)"
                  title="Ver orientação"
                >
                  <fa-icon icon="eye" />
                </button>
                <span v-else class="faixa-row__view-btn--empty">—</span>
              </span>
              <span class="faixa-row__acoes">
                <button class="faixa-row__btn faixa-row__btn--edit" @click="startEdit(regra.originalIndex)" title="Editar">
                  <fa-icon icon="pencil-alt" />
                </button>
                <button class="faixa-row__btn faixa-row__btn--remove" @click="removeFaixa(regra.originalIndex)" title="Remover">
                  <fa-icon icon="times" />
                </button>
              </span>
            </div>
            <div v-if="regra.orientacao && expandedOrientacaoIndex === regra.originalIndex" class="faixa-row__orientacao">
              <fa-icon icon="info-circle" class="faixa-row__orientacao-icon" />
              <span>{{ regra.orientacao }}</span>
            </div>
          </template>

          <!-- Edit Mode -->
          <template v-else>
            <div class="faixa-row">
              <span class="faixa-row__swatch" :style="{ background: editFaixa.cor }"></span>
              <span class="faixa-row__edit-field faixa-row__edit-field--char">
                <input
                  type="text"
                  class="form-control faixa-row__char-input"
                  v-model="editFaixa.indicador"
                  :maxlength="32"
                  @input="normalizarIndicador('editFaixa')"
                />
              </span>
              <span class="faixa-row__edit-field faixa-row__edit-field--label">
                <input type="text" class="form-control" v-model="editFaixa.label" maxlength="100" />
              </span>
              <span class="faixa-row__edit-field faixa-row__edit-field--margem">
                <input
                  type="number"
                  class="form-control"
                  step="0.01"
                  v-model.number="editFaixa.margemMinima"
                  placeholder="0.00"
                />
              </span>
              <span class="faixa-row__edit-field faixa-row__edit-field--cor">
                <ColorInput v-model="editFaixa.cor" />
              </span>
              <span class="faixa-row__orientacao-col"></span>
              <span class="faixa-row__acoes">
                <button class="faixa-row__btn faixa-row__btn--save" @click="saveEdit" :disabled="!canSaveEdit" title="Salvar">
                  <fa-icon icon="check" />
                </button>
                <button class="faixa-row__btn faixa-row__btn--cancel" @click="cancelEdit" title="Cancelar">
                  <fa-icon icon="times" />
                </button>
              </span>
            </div>
            <div class="faixa-row__edit-orientacao">
              <label>Orientação <span class="faixa-form__optional">(opcional)</span></label>
              <textarea
                ref="editOrientacaoTextarea"
                class="form-control faixa-row__orientacao-textarea"
                v-model="editFaixa.orientacao"
                placeholder="Ex: Margem abaixo do ideal. Antes de enviar para análise, tente renegociar com o cliente: ajustar quantidades, oferecer alternativas mais rentáveis ou rever descontos aplicados."
                rows="2"
                maxlength="500"
                @input="autoResize($event.target)"
              ></textarea>
              <span class="faixa-form__counter" :class="{ 'faixa-form__counter--limit': (editFaixa.orientacao || '').length >= 500 }">
                {{ (editFaixa.orientacao || '').length }}/500
              </span>
            </div>
          </template>
        </div>
      </div>

      <div v-else class="faixa-empty">
        <CIcon name="cil-layers" size="xl" class="faixa-empty__icon" />
        <p>Nenhuma faixa cadastrada</p>
        <p class="faixa-empty__hint">Adicione faixas de lucratividade para definir os limites de margem</p>
      </div>

      <div v-if="editError" class="faixa-edit-error">
        <CIcon name="cil-warning" size="sm" class="mr-1" />
        {{ editError }}
      </div>
    </div>

    <!-- Thresholds -->
    <div class="faixa-thresholds" v-if="form.regras.length > 0">
      <div class="faixa-thresholds__label">Configuração de limites</div>
      <div class="faixa-thresholds__row">
        <div class="faixa-thresholds__field">
          <label>Analisa a partir de:</label>
          <select class="form-control" v-model="form.margemMinimaAnalisar">
            <option :value="null">Sem faixa de análise</option>
            <option
              v-for="opcao in opcoesMargemMinima"
              :key="'analisa-' + opcao.value"
              :value="opcao.value"
            >
              {{ opcao.label }} ({{ opcao.value }}%)
            </option>
          </select>
          <span class="faixa-thresholds__hint">
            Opcional — pedidos nesta margem vão para análise do supervisor
          </span>
        </div>

        <div class="faixa-thresholds__field">
          <label>Libera a partir de: <span class="required">*</span></label>
          <select class="form-control" v-model="form.margemMinimaLiberar">
            <option :value="null" disabled>Selecione...</option>
            <option
              v-for="opcao in opcoesMargemMinima"
              :key="'libera-' + opcao.value"
              :value="opcao.value"
            >
              {{ opcao.label }} ({{ opcao.value }}%)
            </option>
          </select>
          <span class="faixa-thresholds__hint">
            Obrigatório — pedidos a partir desta margem são liberados automaticamente
          </span>
        </div>
      </div>

      <div v-if="erroThresholds" class="faixa-thresholds__error">
        <CIcon name="cil-warning" size="sm" class="mr-1" />
        {{ erroThresholds }}
      </div>
    </div>
  </div>
</template>

<script>
import ColorInput from "@/components/ColorInput.vue";

export default {
  name: "StepRegras",
  components: { ColorInput },
  props: {
    form: { type: Object, required: true },
  },
  data() {
    return {
      newFaixa: this.emptyFaixa(),
      editingIndex: null,
      editFaixa: null,
      expandedOrientacaoIndex: null,
      editError: null,
    };
  },
  computed: {
    canAddFaixa() {
      return (
        !!this.newFaixa.label &&
        !!this.newFaixa.cor &&
        this.indicadorTemUmCaractereOuEmoji(this.newFaixa.indicador) &&
        this.newFaixa.margemMinima !== null &&
        this.newFaixa.margemMinima !== ''
      );
    },
    canSaveEdit() {
      if (!this.editFaixa) return false;
      return (
        !!this.editFaixa.label &&
        !!this.editFaixa.cor &&
        this.indicadorTemUmCaractereOuEmoji(this.editFaixa.indicador) &&
        this.editFaixa.margemMinima !== null &&
        this.editFaixa.margemMinima !== ''
      );
    },
    sortedRegras() {
      return [...this.form.regras]
        .map((r, i) => ({ ...r, originalIndex: i }))
        .sort((a, b) => a.margemMinima - b.margemMinima);
    },
    opcoesMargemMinima() {
      return this.form.regras
        .map((r) => ({ value: r.margemMinima, label: r.label }))
        .sort((a, b) => a.value - b.value);
    },
    erroThresholds() {
      if (
        this.form.margemMinimaAnalisar !== null &&
        this.form.margemMinimaLiberar !== null &&
        this.form.margemMinimaAnalisar >= this.form.margemMinimaLiberar
      ) {
        return "'Analisa a partir de' deve ser menor que 'Libera a partir de'";
      }
      return null;
    },
  },
  watch: {
    "form.regras": {
      deep: true,
      handler() {
        const margens = this.form.regras.map((r) => r.margemMinima);
        if (
          this.form.margemMinimaAnalisar !== null &&
          !margens.includes(this.form.margemMinimaAnalisar)
        ) {
          this.form.margemMinimaAnalisar = null;
        }
        if (
          this.form.margemMinimaLiberar !== null &&
          !margens.includes(this.form.margemMinimaLiberar)
        ) {
          this.form.margemMinimaLiberar = null;
        }
      },
    },
  },
  methods: {
    // Um "grapheme" representa o que o usuário enxerga como UM caractere na
    // tela. Para letras ASCII (A, B, C) cada caractere é um grapheme. Já um
    // emoji como 🚀 ocupa 2 code points em JavaScript, e emojis compostos
    // como 👨‍👩‍👧 chegam a 5+ code points unidos por ZWJ. Esses helpers
    // garantem que tratamos um emoji inteiro como uma unidade só.
    extrairPrimeiroCaractereVisivel(texto) {
      if (!texto) return '';
      if (typeof Intl !== 'undefined' && Intl.Segmenter) {
        const segmentador = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
        const primeiroSegmento = segmentador.segment(texto)[Symbol.iterator]().next().value;
        return primeiroSegmento ? primeiroSegmento.segment : '';
      }
      // Fallback para navegadores sem Intl.Segmenter: itera por code points.
      // Não cobre ZWJ corretamente, mas serve para emojis simples.
      return [...texto][0] || '';
    },
    contarCaracteresVisiveis(texto) {
      if (!texto) return 0;
      if (typeof Intl !== 'undefined' && Intl.Segmenter) {
        const segmentador = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
        return [...segmentador.segment(texto)].length;
      }
      return [...texto].length;
    },
    indicadorTemUmCaractereOuEmoji(texto) {
      return this.contarCaracteresVisiveis(texto) === 1;
    },
    isLetraAlfabetica(caractere) {
      // \p{L} casa qualquer letra Unicode (latina, cirílica, etc.) e exclui
      // dígitos, símbolos e emojis.
      return typeof caractere === 'string' && caractere.length === 1 && /\p{L}/u.test(caractere);
    },
    // Mantém o input do indicador limitado a 1 caractere visível. Se for letra,
    // converte para maiúscula (padrão do sistema). Se for emoji, preserva como
    // digitado. Chamado no @input dos campos newFaixa.indicador e editFaixa.indicador.
    normalizarIndicador(nomeDoFormulario) {
      const formulario = this[nomeDoFormulario];
      if (!formulario) return;
      const primeiroCaractere = this.extrairPrimeiroCaractereVisivel(formulario.indicador);
      if (!primeiroCaractere) {
        formulario.indicador = '';
        return;
      }
      formulario.indicador = this.isLetraAlfabetica(primeiroCaractere)
        ? primeiroCaractere.toUpperCase()
        : primeiroCaractere;
    },
    emptyFaixa() {
      return {
        label: "",
        cor: "#FF1744",
        margemMinima: 0,
        indicador: "",
        orientacao: "",
      };
    },
    addFaixa() {
      if (!this.canAddFaixa) return;
      this.form.regras.push({ ...this.newFaixa });
      this.newFaixa = this.emptyFaixa();
      this.$nextTick(() => {
        if (this.$refs.newOrientacaoTextarea) {
          this.$refs.newOrientacaoTextarea.style.height = "auto";
        }
      });
    },
    removeFaixa(originalIndex) {
      if (this.editingIndex !== null && this.editingIndex !== originalIndex) {
        const result = this.commitInlineEdit();
        if (!result.ok) return;
      }
      const wasEditing = this.editingIndex === originalIndex;
      this.form.regras.splice(originalIndex, 1);
      if (wasEditing) {
        this.cancelEdit();
      } else if (this.editingIndex !== null && originalIndex < this.editingIndex) {
        this.editingIndex -= 1;
      }
      if (this.expandedOrientacaoIndex === originalIndex) {
        this.expandedOrientacaoIndex = null;
      } else if (
        this.expandedOrientacaoIndex !== null &&
        originalIndex < this.expandedOrientacaoIndex
      ) {
        this.expandedOrientacaoIndex -= 1;
      }
    },
    toggleOrientacao(originalIndex) {
      this.expandedOrientacaoIndex =
        this.expandedOrientacaoIndex === originalIndex ? null : originalIndex;
    },
    startEdit(originalIndex) {
      if (this.editingIndex !== null && this.editingIndex !== originalIndex) {
        const result = this.commitInlineEdit();
        if (!result.ok) return;
      }
      this.editingIndex = originalIndex;
      this.editFaixa = {
        indicador: "",
        orientacao: "",
        ...this.form.regras[originalIndex],
      };
      this.editError = null;
      this.$nextTick(() => {
        if (this.$refs.editOrientacaoTextarea) {
          this.autoResize(this.$refs.editOrientacaoTextarea);
        }
      });
    },
    saveEdit() {
      if (!this.canSaveEdit) return;
      if (this.editingIndex !== null && this.editFaixa) {
        this.form.regras.splice(this.editingIndex, 1, { ...this.editFaixa });
      }
      this.cancelEdit();
    },
    cancelEdit() {
      this.editingIndex = null;
      this.editFaixa = null;
      this.editError = null;
    },
    commitInlineEdit() {
      if (this.editingIndex === null) return { ok: true };
      if (!this.canSaveEdit) {
        this.editError =
          "Faixa em edição tem campos obrigatórios faltando. Salve ou cancele a edição antes.";
        setTimeout(() => {
          if (this.editError) this.editError = null;
        }, 5000);
        return { ok: false, reason: "invalid" };
      }
      this.form.regras.splice(this.editingIndex, 1, { ...this.editFaixa });
      this.cancelEdit();
      return { ok: true };
    },
    hasPendingEdit() {
      return this.editingIndex !== null;
    },
    autoResize(el) {
      if (!el) return;
      el.style.height = "auto";
      el.style.height = el.scrollHeight + "px";
    },
    getStatusFaixa(margemMinima) {
      const { margemMinimaAnalisar, margemMinimaLiberar } = this.form;

      if (margemMinimaLiberar === null) return null;

      if (margemMinima >= margemMinimaLiberar) {
        return { label: "Liberado para envio", color: "#00C853", bg: "#00C85314" };
      }

      if (margemMinimaAnalisar !== null && margemMinima >= margemMinimaAnalisar) {
        return { label: "Disponível para análise", color: "#FF9800", bg: "#FF980014" };
      }

      return { label: "Bloqueado", color: "#FF1744", bg: "#FF174414" };
    },
  },
};
</script>

<style scoped>
.step-regras {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ===== ADD FORM ===== */
.faixa-form {
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  padding: 16px;
  background: #f9fafb;
}

.faixa-form__title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #888;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
}

.faixa-form__row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.faixa-form__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.faixa-form__field label {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.faixa-form__field--label {
  flex: 1;
}

.faixa-form__field--char {
  flex: 0 0 64px;
}

.faixa-form__field--cor {
  flex: 0 0 140px;
}

.faixa-form__field--margem {
  flex: 0 0 120px;
}

.faixa-form__field--orientacao {
  flex: 1;
}

.faixa-form__row--orientacao {
  margin-top: 10px;
}

.faixa-form__optional {
  font-weight: 400;
  color: #98a2b3;
  text-transform: none;
  letter-spacing: 0;
  font-size: 10px;
}

.faixa-form__counter {
  align-self: flex-end;
  font-size: 11px;
  color: #98a2b3;
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
}

.faixa-form__counter--limit {
  color: #dc2626;
  font-weight: 600;
}

.faixa-form__char-input {
  text-align: center;
  font-family: "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", "SFMono-Regular", Menlo, Consolas, monospace;
  font-weight: 700;
  padding: 0 6px !important;
}

.faixa-form__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

.faixa-form .form-control {
  height: 35px;
  font-size: 13px;
  border-radius: 4px;
  border: 1px solid #d8dbe0;
  padding: 0 10px;
  background-color: #fff;
  transition: border-color 0.15s ease;
  width: 100%;
  line-height: 1.5;
  box-sizing: border-box;
}

.faixa-form textarea.form-control {
  height: auto;
  min-height: 64px;
  padding: 8px 10px;
  resize: vertical;
  font-family: inherit;
  line-height: 1.5;
}

.faixa-form .form-control:focus {
  border-color: #3a3e81;
  outline: none;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.1);
}

.faixa-form .form-control:disabled {
  background-color: #f2f4f7;
  cursor: not-allowed;
}

.faixa-form__action {
  flex-shrink: 0;
}

.faixa-form__add-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #3a3e81;
  border-radius: 6px;
  background: #3a3e81;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.faixa-form__add-btn:hover:not(:disabled) {
  background: #2e3270;
}

.faixa-form__add-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ===== FAIXAS LIST ===== */
.faixa-selected__label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #888;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.faixa-list {
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  overflow: visible;
  background: #fff;
}

.faixa-list__header {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e4e7eb;
  gap: 10px;
}

.faixa-list__col {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: #888;
  letter-spacing: 0.5px;
}

.faixa-list__col--cor { flex: 0 0 24px; }
.faixa-list__col--char { flex: 0 0 44px; text-align: center; }
.faixa-list__col--label { flex: 1; }
.faixa-list__col--margem { flex: 0 0 100px; }
.faixa-list__col--status { flex: 0 0 170px; }
.faixa-list__col--orientacao { flex: 0 0 90px; text-align: center; }
.faixa-list__col--acoes { flex: 0 0 70px; }

.faixa-row-wrapper {
  border-bottom: 1px solid #f2f4f7;
  transition: background 0.1s;
}

.faixa-row-wrapper:last-child {
  border-bottom: none;
}

.faixa-row-wrapper:hover {
  background: #f9fafb;
}

.faixa-row-wrapper--editing {
  background: #f5f6fb;
}

.faixa-row-wrapper--editing:hover {
  background: #f5f6fb;
}

.faixa-row {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  gap: 10px;
}

.faixa-row__swatch {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.faixa-row__char {
  flex: 0 0 44px;
  display: flex;
  justify-content: center;
}

.faixa-row__char-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 6px;
  border-radius: 6px;
  background: #f2f2f8;
  color: #3a3e81;
  font-family: "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", "SFMono-Regular", Menlo, Consolas, monospace;
  font-size: 14px;
  font-weight: 700;
  border: 1px solid #e4e4f1;
}

.faixa-row__label {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: #1d2939;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.faixa-row__orientacao {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 0 16px 10px 16px;
  font-size: 12px;
  color: #667085;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.faixa-row__orientacao-icon {
  color: #98a2b3;
  margin-top: 3px;
  flex-shrink: 0;
}

.faixa-row__margem {
  flex: 0 0 100px;
  font-size: 13px;
  color: #667085;
}

.faixa-row__status {
  flex: 0 0 170px;
}

.faixa-row__orientacao-col {
  flex: 0 0 90px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.faixa-row__view-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 28px;
  padding: 0;
  border: 1px solid #dddce9;
  border-radius: 6px;
  background: #fff;
  color: #3a3e81;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.15s ease;
}

.faixa-row__view-btn:hover {
  background: #f2f2f8;
  border-color: #3a3e81;
}

.faixa-row__view-btn--active {
  background: #3a3e81;
  border-color: #3a3e81;
  color: #fff;
}

.faixa-row__view-btn--active:hover {
  background: #2e3270;
  border-color: #2e3270;
}

.faixa-row__view-btn--empty {
  color: #ccc;
  font-size: 13px;
}

.faixa-status-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 12px;
  border: 1px solid;
  white-space: nowrap;
}

.faixa-status-badge--empty {
  background: transparent;
  color: #ccc;
  border-color: transparent;
}

.faixa-row__acoes {
  flex: 0 0 70px;
  display: flex;
  gap: 4px;
  justify-content: flex-end;
}

.faixa-row__btn {
  background: none;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 6px 8px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.faixa-row__btn--edit {
  color: #3a3e81;
}

.faixa-row__btn--edit:hover {
  background: #f2f2f8;
  border-color: #dddce9;
}

.faixa-row__btn--remove {
  color: #98a2b3;
}

.faixa-row__btn--remove:hover {
  color: #e74c3c;
  border-color: #e74c3c;
  background: #fef2f2;
}

.faixa-row__btn--save {
  color: #00C853;
}

.faixa-row__btn--save:hover {
  background: #e8f5e9;
  border-color: #00C853;
}

.faixa-row__btn--cancel {
  color: #98a2b3;
}

.faixa-row__btn--cancel:hover {
  color: #e74c3c;
  background: #fef2f2;
  border-color: #e74c3c;
}

/* Edit mode inline fields */
.faixa-row__edit-field {
  display: flex;
  align-items: center;
  gap: 6px;
}

.faixa-row__edit-field--label {
  flex: 1;
}

.faixa-row__edit-field--char {
  flex: 0 0 44px;
}

.faixa-row__edit-field--cor {
  flex: 0 0 170px;
}

.faixa-row__edit-field--margem {
  flex: 0 0 100px;
}

.faixa-row__edit-field .form-control {
  height: 30px;
  font-size: 12px;
  border-radius: 4px;
  border: 1px solid #d8dbe0;
  padding: 0 8px;
  background-color: #fff;
  width: 100%;
  line-height: 1.5;
  box-sizing: border-box;
  transition: border-color 0.15s ease;
}

.faixa-row__edit-field .form-control:focus {
  border-color: #3a3e81;
  outline: none;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.1);
}

.faixa-row__edit-field .form-control:disabled {
  background-color: #f2f4f7;
}

.faixa-row__char-input {
  text-align: center;
  font-family: "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", "SFMono-Regular", Menlo, Consolas, monospace;
  font-weight: 700;
  padding: 0 4px !important;
}

.faixa-row__edit-orientacao {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 16px 12px 16px;
}

.faixa-row__edit-orientacao label {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.faixa-row__orientacao-textarea {
  height: auto;
  min-height: 60px;
  font-size: 13px;
  border-radius: 4px;
  border: 1px solid #d8dbe0;
  padding: 8px 10px;
  background-color: #fff;
  width: 100%;
  resize: vertical;
  font-family: inherit;
  line-height: 1.5;
  box-sizing: border-box;
  transition: border-color 0.15s ease;
}

.faixa-row__orientacao-textarea:focus {
  border-color: #3a3e81;
  outline: none;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.1);
}

.faixa-row__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ===== EMPTY STATE ===== */
.faixa-empty {
  text-align: center;
  padding: 32px 20px;
  color: #98a2b3;
  border: 1px dashed #e4e7eb;
  border-radius: 10px;
}

.faixa-empty__icon {
  color: #d0d5dd;
  margin-bottom: 8px;
}

.faixa-empty p {
  font-size: 13px;
  margin: 0;
}

.faixa-empty__hint {
  font-size: 12px;
  color: #b0b8c4;
  margin-top: 4px;
}

.faixa-edit-error {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 10px;
  padding: 8px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 13px;
}

/* ===== THRESHOLDS ===== */
.faixa-thresholds {
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  padding: 16px;
  background: #f9fafb;
}

.faixa-thresholds__label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #888;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
}

.faixa-thresholds__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.faixa-thresholds__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.faixa-thresholds__field label {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.faixa-thresholds__field .form-control {
  font-size: 13px;
  border-radius: 6px;
  border: 1px solid #dddce9;
  padding: 8px 10px;
}

.faixa-thresholds__field .form-control:focus {
  border-color: #3a3e81;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.1);
}

.faixa-thresholds__hint {
  font-size: 11px;
  color: #98a2b3;
}

.faixa-thresholds__error {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 12px;
  padding: 8px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 13px;
}

.required {
  color: #e74c3c;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .faixa-form__row {
    flex-direction: column;
    align-items: stretch;
  }

  .faixa-form__field--char,
  .faixa-form__field--cor,
  .faixa-form__field--margem {
    flex: 1;
  }

  .faixa-thresholds__row {
    grid-template-columns: 1fr;
  }

  .faixa-list__header {
    display: none;
  }

  .faixa-row {
    flex-wrap: wrap;
  }

  .faixa-row__orientacao,
  .faixa-row__edit-orientacao {
    padding-left: 16px;
  }
}
</style>
